import { KeyboardEvent, useEffect, useState } from 'react';
import { UseQueryResult } from '@tanstack/react-query';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from 'styled-components';
import Tabs from '../Tabs/Tabs';
import { IconKeys } from '../Icon/IconKeys';
import ItemsList from './ItemsList/ItemsList';
import { useGetAlertsLazy } from '../../../API/hooks/alerts/useGetAlerts';
import { useGetTripStatsLazy } from '../../../API/hooks/trips/useGetTripStats';
import { useGetDeviceConnectivityLazy } from '../../../API/hooks/devices/useGetDeviceConnectivity';
import { useGetTripsLazy } from '../../../API/hooks/trips/useGetTrips';
import { useGetVideosLazy } from '../../../API/hooks/videos/useGetVideos';
import { useGetCalendar } from './hooks';
import AlertsItem from '../../../pages/Alerts/AlertsItem/AlertsItem';
import DriverStatsItem from '../DriversItem/DriverStatsItem';
import DeviceConnectivityItem from '../DeviceConnectivityItem/DeviceConnectivityItem';
import VehicleStatsItem from '../VehiclesItem/VehicleStatsItem';
import TripsItem from '../../../pages/Trips/TripsItem/TripsItem';
import VideoItem from '../../../pages/Videos/VideoItem/VideoItem';
import NoteItem from '../../Notes/NoteItem/NoteItem';
import AddQuickNote from '../../Notes/AddQuickNote/AddQuickNote';
import Tags from '../Tags';
import { ListItemWrapper } from '../../ui/ListItemWrapper/ListItemWrapper';
import Icon from '../Icon/Icon';
import {
  getHoursAndMinutes, getFormattedDateWithComma, getTimeRangeText, getTimeZone, getEndOfTheDay,
} from '../../../utils/dateUtils';
import S from './DetailsWidgetStyles';
import { logger } from '../../../API/services/SharedService';
import { CalendarItemType } from './types';
import { getTimelineIconColor, getTimelineIconKey } from './timelineIcon';
import { ThemeType } from '../../../theme/themeType';
import GeofenceEventItem from '../GeofenceEventItem/GeofenceEventItem';
import { useGetGeofenceEventsLazy } from '../../../API/hooks/geofences/useGetGeofenceEvents';
import SearchFilter from '../SearchFilter/SearchFilter';
import useGetFleet from '../../../API/hooks/useGetFleetDetails';
import useGetAlertsReport from '../../../API/hooks/reports/useGetAlertsReport';
import useGetDevicesReport from '../../../API/hooks/reports/useGetDevicesReport';
import useGetDriversReport from '../../../API/hooks/reports/useGetDriversReport';
import useGetVehiclesReport from '../../../API/hooks/reports/useGetVehiclesReport';
import useGetTripsReport from '../../../API/hooks/reports/useGetTripsReport';
import useGetVideosReport from '../../../API/hooks/reports/useGetVideosReport';
import useGetGeofenceEventsReport from '../../../API/hooks/reports/useGetGeofenceEventsReport';
import useGetNotesReport from '../../../API/hooks/reports/useGetNotesReport';
import { useGetNotesByQueryLazy } from '../../../API/hooks/notes/useGetNotesByQuery';
import { useAlertFocus } from '../../../API/hooks/useAlertFocus';
import alertFocusStore from '../../../store/AlertFocusStore';
import { buildAlertDetailsLocationState } from '../../../utils/alertNavigation';

function CalendarItem({ item }: { item?: CalendarItemType }): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme() as ThemeType;
  const iconKey = getTimelineIconKey(item);
  const iconColor = getTimelineIconColor(item, theme);
  const isTripStartOrEnd = item?.type === 'trip';
  const { pinned, enabled: alertFocusEnabled } = useAlertFocus();
  const focusableAlert = alertFocusEnabled ? item?.alert : undefined;
  const isPinned = !!focusableAlert && pinned?._id === focusableAlert._id;

  const openDetails = (): void => {
    if (!item) return;

    if (item.type === 'trip') {
      navigate(`/trips/${item.detailsId}`, { state: { isMergedTrip: item.isMergedTrip } });
      return;
    }

    navigate(`/alerts/${item.detailsId}`, {
      state: buildAlertDetailsLocationState(
        location.pathname,
        location.search,
        location.state,
      ),
    });
  };

  const handleRowKeyDown = (event: KeyboardEvent<HTMLLIElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openDetails();
    }
  };

  const timelineIcon = focusableAlert ? (
    <button
      type="button"
      className="entity-icon map-focus-button"
      onClick={(event) => {
        event.stopPropagation();
        alertFocusStore.togglePinned(focusableAlert);
      }}
      title={isPinned ? 'Remove this alert from the map' : 'Keep this alert on the map'}
      aria-label={isPinned ? 'Remove this alert from the map' : 'Keep this alert on the map'}
    >
      <Icon iconKey={iconKey} />
      <div className="line" />
    </button>
  ) : (
    <div className="entity-icon">
      <Icon iconKey={iconKey} />
      <div className="line" />
    </div>
  );

  return (
    <S.CalendarWrapper
      $iconColor={iconColor}
      className={`${isTripStartOrEnd ? '' : 'indented'}${isPinned ? ' map-pinned' : ''}`}
      onMouseEnter={focusableAlert ? () => alertFocusStore.setHovered(focusableAlert) : undefined}
      onMouseLeave={focusableAlert ? () => alertFocusStore.clearHovered(focusableAlert._id) : undefined}
    >
      {item && (
        <ListItemWrapper
          onClick={openDetails}
          onKeyDown={handleRowKeyDown}
          role="link"
          tabIndex={0}
          title={`Open ${item.type} details`}
        >
          {timelineIcon}
          <Tags.LabeledIcon
            label={`${getHoursAndMinutes(item.date)} (${getTimeZone(item.date)})`}
            icon={IconKeys.TIME}
            flexible
          />
          <Tags.LabeledIcon
            label={getFormattedDateWithComma(item.date)}
            icon={IconKeys.DATE}
          />
          <Tags.ItemOverview
            avatar={item.thumbnailUrl || ''}
            avatarAlt="image"
            title={item.title}
            subtitle={item.subtitle || ''}
          />
        </ListItemWrapper>
      )}
    </S.CalendarWrapper>
  );
}

type ShowTabsType = {
  calendar?: boolean;
  alert?: boolean;
  driver?: boolean;
  vehicle?: boolean;
  trip?: boolean;
  video?: boolean;
  note?: boolean;
  connectivity?: boolean;
  geofence?: boolean;
};

type Props = {
  fleetId: string;
  entityId: string;
  entity: string;
  showTabs: ShowTabsType;
  filters?: Record<string, string[] | string>;
  filterField?: string;
  filterValue?: string;
  noteEntityName?: string;
  noteEntityId?: string;
  hideSearch?: boolean;
  hideAlertEntityDetails?: boolean;
  hidePeriodInListHeading?: boolean;
  calendarSeedItems?: CalendarItemType[];
  inclusiveEventDateStart?: boolean;
};

const tabsConfig = [{
  id: 'calendar',
  label: 'Timeline',
  query: useGetCalendar,
  Component: CalendarItem,
}, {
  id: 'alert',
  label: 'Alerts',
  query: useGetAlertsLazy,
  Component: AlertsItem,
}, {
  id: 'driver',
  label: 'Drivers',
  query: useGetTripStatsLazy,
  Component: DriverStatsItem,
}, {
  id: 'vehicle',
  label: 'Vehicles',
  query: useGetTripStatsLazy,
  Component: VehicleStatsItem,
}, {
  id: 'trip',
  label: 'Trips',
  query: useGetTripsLazy,
  Component: TripsItem,
}, {
  id: 'video',
  label: 'VODs',
  query: useGetVideosLazy,
  Component: VideoItem,
}, {
  id: 'note',
  label: 'Notes',
  query: useGetNotesByQueryLazy,
  Component: NoteItem,
}, {
  id: 'connectivity',
  label: 'Connectivity history',
  query: useGetDeviceConnectivityLazy,
  Component: DeviceConnectivityItem,
}, {
  id: 'geofence',
  label: 'Geofence',
  query: useGetGeofenceEventsLazy,
  Component: GeofenceEventItem,
}];

const INITIAL_FILTERS: Record<string, any> = {
  calendar: { sortBy: ['createdAt_DESC'] },
  alert: { sortBy: ['eventDate_DESC'] },
  driver: { sortBy: ['updatedAt_DESC'] },
  vehicle: { sortBy: ['updatedAt_DESC'] },
  trip: { sortBy: ['startAt_DESC'] },
  video: { sortBy: ['createdAt_DESC'] },
  note: { sortBy: ['createdAt_DESC'] },
  connectivity: { sortBy: ['createdAt_DESC'] },
  geofence: { sortBy: ['createdAt_DESC'] },
};

const getFilterField = (entity: string, tab: string): string => {
  const field = `${entity}Id`;

  return field;
};

const generatePayload = (
  id: string,
  entity: string,
  entityId: string,
  noteEntity?: { entityName: string; entityId: string },
): Record<string, string | number> => {
  let payload = {};

  if (id === 'note') {
    payload = {
      entityName: noteEntity?.entityName || `${entity}s`,
      entityId: noteEntity?.entityId || entityId,
    };
  }
  if (id === 'driver') {
    payload = { entityKey: `${id}Id` };
  }
  if (id === 'vehicle') {
    payload = { entityKey: `${id}Id` };
  }

  return payload;
};

function DetailsWidget({
  fleetId, entity, entityId, showTabs, filters,
  filterField: filterFieldOverride, filterValue: filterValueOverride,
  noteEntityName, noteEntityId, hideSearch = false, hideAlertEntityDetails = false,
  hidePeriodInListHeading = false, calendarSeedItems,
  inclusiveEventDateStart = false,
}: Props): JSX.Element {
  const tabsAllowInputFilter = ['calendar', 'alert', 'trip', 'geofence'];
  const tabsAllowReport = ['alert', 'trip', 'video', 'geofence', 'note'];
  const [filterWithInput, changeFilterWithInput] = useState<Record<string, string[] | string> | undefined>(filters);
  const [inputFilterText, changeInputFilterText] = useState('');
  const [showSearch, setShowSearch] = useState(true);
  const { data: fleetData } = useGetFleet(fleetId);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const setFilters = (f: any): void => {
    changeFilterWithInput(f);
    changeInputFilterText(f.input[0]);
  };

  useEffect(() => {
    const updatedFilter = { ...filters };
    if (inputFilterText !== '' && updatedFilter) {
      updatedFilter.input = [inputFilterText];
    }
    changeFilterWithInput(updatedFilter);
    // changeInputFilterText('...');
    // setTimeout(() => changeInputFilterText(''));
  }, [filters, inputFilterText]);

  const setTabs = (): any => {
    const tabs = tabsConfig
      .filter(({ id }) => showTabs[id as keyof ShowTabsType]).map(({
        id, label, query, Component,
      }) => {
        const activeFilter = id && tabsAllowInputFilter.includes(id) ? filterWithInput : filters;
        const filterField = filterFieldOverride || getFilterField(entity, id);
        const filterEntityValue = filterValueOverride || entityId;
        const resolvedNoteEntityName = noteEntityName || `${entity}s`;
        const resolvedNoteEntityId = noteEntityId || entityId;
        const currentFilters = id === 'note'
          ? { ...INITIAL_FILTERS[id] }
          : { ...INITIAL_FILTERS[id], ...activeFilter };
        let customQuery = `&filter[9][field]=${filterField}&filter[9][operator]=$eq&filter[9][value]=${filterEntityValue}`;
        switch (id) {
          case 'note':
            customQuery = `&filter[10][field]=entityName&filter[10][operator]=$eq&filter[10][value]=${resolvedNoteEntityName}&filter[11][field]=entityId&filter[11][operator]=$eq&filter[11][value]=${resolvedNoteEntityId}`;
            break;
          case 'video': {
            const videoFrom = activeFilter?.startDate?.[0] ?? '2020-01-20T05:44:09.509Z';
            const videoTo = activeFilter?.endDate?.[0] ?? getEndOfTheDay().toISOString();
            customQuery = `${customQuery}&from=${videoFrom}&to=${videoTo}&filter[10][field]=alertId&filter[10][operator]=$eq&filter[10][value]=null`;
            break;
          }
          case 'driver':
          case 'vehicle':
            customQuery = `${customQuery}&filter[10][field]=endAt&filter[10][operator]=$ne&filter[10][value]=null`;
            break;
          case 'trip':
          default:
            logger.info('DetailsWidget', 'no extra query');
        }

        let reportRequestEntity: { (fleetId: string, query: string): UseQueryResult<any>; (fleetId: string, query: string): UseQueryResult<any>; (fleetId: string, query: string): UseQueryResult<any>; (fleetId: string, query: string): UseQueryResult<any>; (fleetId: string, query: string): UseQueryResult<any>; (fleetId: string, query: string): UseQueryResult<any>; (fleetId: string, query: string): UseQueryResult<any>; (fleetId: string, entityName: string, entityId: string, query: string): UseQueryResult<any>; };

        switch (id) {
          case 'alert':
            reportRequestEntity = useGetAlertsReport;
            break;
          case 'driver':
            reportRequestEntity = useGetDriversReport;
            break;
          case 'vehicle':
            reportRequestEntity = useGetVehiclesReport;
            break;
          case 'device':
            reportRequestEntity = useGetDevicesReport;
            break;
          case 'trip':
            reportRequestEntity = useGetTripsReport;
            break;
          case 'video':
            reportRequestEntity = useGetVideosReport;
            break;
          case 'geofence':
            reportRequestEntity = useGetGeofenceEventsReport;
            break;
          case 'note':
            reportRequestEntity = useGetNotesReport;
            break;
          default:
            logger.info('DetailsWidget', 'Unknown tab id');
        }

        const period = (filters?.period as string[] | undefined)?.[0];
        const config = (): JSX.Element => (
          <div>
            {(id === 'note') && (
              <AddQuickNote
                fleetId={fleetId}
                entityName={resolvedNoteEntityName}
                entityId={resolvedNoteEntityId}
              />
            )}
            <ItemsList
              listEntity={id}
              fleetId={fleetId}
              payload={generatePayload(id, entity, entityId, id === 'note'
                ? { entityName: resolvedNoteEntityName, entityId: resolvedNoteEntityId }
                : undefined)}
              getQuery={query}
              isRow={id === 'video'}
              filters={currentFilters}
              deps={id === 'note'
                ? [{ entityName: resolvedNoteEntityName, entityId: resolvedNoteEntityId }]
                : [activeFilter, id, customQuery]}
              customQuery={customQuery}
              hasCreateReport={tabsAllowReport.includes(id)}
              reportEndpoint={reportRequestEntity}
              reportName={`${fleetData?.name || 'fleet_na'}_${entity}_${entityId}_${id}s_${getTimeRangeText(activeFilter, true)}`}
              hidePeriodInListHeading={hidePeriodInListHeading}
              calendarSeedItems={calendarSeedItems}
              inclusiveEventDateStart={inclusiveEventDateStart}
              extraChildProps={(() => {
                const props: Record<string, any> = {};
                if (id === 'vehicle' || id === 'driver') {
                  props.period = period;
                }
                if (id === 'alert' && !hideAlertEntityDetails) {
                  if (entity === 'driver') {
                    props.showVehicleCard = true;
                  }
                  if (entity === 'device') {
                    props.includeVehicleDetails = true;
                  }
                }
                if (id === 'alert' && hideAlertEntityDetails) {
                  props.hideEntityDetails = true;
                }
                return Object.keys(props).length ? props : undefined;
              })()}
            >
              {id === 'trip' ? <Component /> : <Component lightVersion />}
            </ItemsList>
          </div>
        );

        return {
          label,
          id,
          content: config(),
        };
      });
    return tabs;
  };

  const tabsContent = setTabs();

  return (
    <S.Wrapper>
      {!hideSearch && (
        <div className="search-filter">
          {showSearch && (
            <SearchFilter
              filters={filters as Record<string, string[]>}
              setFilters={setFilters}
              defaultText={inputFilterText}
            />
          )}
        </div>
      )}
      <Tabs
        tabs={tabsContent}
        activeId={searchParams.get('tab') || tabsContent?.[0]?.id}
        onChange={(id: string) => {
          setSearchParams({ tab: id }, { replace: true, state: location.state });
          setShowSearch(tabsAllowInputFilter.includes(id));
          changeFilterWithInput(filters);
          changeInputFilterText('...');
          setTimeout(() => changeInputFilterText(''));
        }}
      />
    </S.Wrapper>
  );
}

export default DetailsWidget;
